import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  LogOut,
  User,
  Crown,
  ExternalLink,
  Check,
} from "lucide-react";
import { isPaidAccess } from "@/lib/plan";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

type StoreSwitcherProps = {
  compact?: boolean;
  className?: string;
};

export function StoreSwitcher({ compact = false, className }: StoreSwitcherProps) {
  const { user, logout, currentStore, stores, switchStore } = useAuth();
  const showUpgrade =
    currentStore?.isOwner &&
    user &&
    !isPaidAccess(user) &&
    user.plan !== "custom";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center rounded-2xl text-left text-foreground transition-colors hover:bg-foreground/[0.06]",
            compact ? "w-auto shrink-0 gap-1.5 px-1 py-1" : "w-full gap-2.5 px-2 py-2",
            className,
          )}
        >
          <Avatar
            className={cn(
              "light-surface shrink-0 rounded-xl bg-white ring-1 ring-black/10",
              compact ? "h-8 w-8" : "h-9 w-9",
            )}
          >
            <AvatarImage
              src={currentStore?.logo}
              alt={currentStore?.storeName}
              className="rounded-xl bg-white"
            />
            <AvatarFallback className="rounded-xl bg-white text-sm font-bold text-[#451892]">
              {currentStore?.storeName?.charAt(0) ||
                user?.email?.charAt(0) ||
                "U"}
            </AvatarFallback>
          </Avatar>
          {!compact ? (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold leading-tight text-foreground">
                  {currentStore?.storeName}
                </p>
                <p className="mt-0.5 truncate text-[11px] capitalize text-muted-foreground">
                  {user?.role}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </>
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={compact ? "end" : "start"}
        className="w-64 rounded-2xl border-[#EBEBEB] p-1.5 shadow-[0_16px_40px_-20px_rgba(0,0,0,0.18)]"
      >
        <div className="px-2 py-2">
          <p className="text-[13px] font-semibold">{user?.name || user?.email}</p>
          <p className="truncate text-[11px] text-muted-foreground">{user?.email}</p>
        </div>

        {stores.length > 1 && (
          <>
            <DropdownMenuSeparator />
            {stores.map((store) => (
              <DropdownMenuItem
                key={store._id}
                className="cursor-pointer rounded-xl text-[13px]"
                onClick={() => switchStore(store._id)}
              >
                <span className="flex-1 truncate">{store.storeName}</span>
                {currentStore?._id === store._id ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : null}
              </DropdownMenuItem>
            ))}
          </>
        )}

        {currentStore && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer rounded-xl text-[13px]"
              onClick={() =>
                window.open(`https://sqale.shop/${currentStore.url}`, "_blank")
              }
            >
              <ExternalLink className="h-4 w-4" /> Visit store
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        {showUpgrade && (
          <DropdownMenuItem asChild>
            <Link
              to="/dashboard/settings#billing"
              className="flex cursor-pointer items-center gap-2 rounded-xl text-[13px] font-medium text-primary"
            >
              <Crown className="h-4 w-4" />
              Upgrade to Premium
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link
            to="/dashboard/settings#profile"
            className="flex cursor-pointer items-center gap-2 rounded-xl text-[13px]"
          >
            <User className="h-4 w-4" />
            My account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer rounded-xl text-[13px] text-destructive focus:text-destructive"
          onClick={logout}
        >
          <LogOut className="h-4 w-4 text-destructive" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
